import React, { useCallback, useMemo, useState } from 'react'
import { useCommunity } from '~/contexts/CommunityContext'
import '@css/pages/company/CompanyAccountPartners.scss'
import { SizeBreakpoint, useWindowSize } from '~/common/hooks/useWindowSize'
import ManageMembersLink from '~/pages/company/ManageMembersLink'
import { Alert, Form, Modal } from 'react-bootstrap'
import { searchCache, sortMembers } from '~/utils'
import { GetCommunityLeadersDocument, Membership } from '~/api/generated/graphql'
import { useStickyState } from '~/common/hooks/useStickyState'
import searchIcon from '@web/images/community/icon-search.svg'
import searchClear from '@web/images/community/search-clear.svg'
import { CompanyMemberRow } from '~/pages/company/CompanyMemberRow'
import { useQuery } from '@apollo/client'

const CompanyAccountPartners = () => {
  const { breakpoint } = useWindowSize()
  const isCondensed = breakpoint <= SizeBreakpoint.md
  const isMedium = isCondensed && SizeBreakpoint.sm < breakpoint
  const isNotXLarge = breakpoint <= SizeBreakpoint.lg
  const { loading: loadingCommunity, error, communityId, isVeeva } = useCommunity()
  const ACCOUNT_PARTNERS_SEARCH_KEY = `${communityId}PartnersSearchKey`
  const [searchText, setSearchText] = useStickyState('', ACCOUNT_PARTNERS_SEARCH_KEY)
  const showSearchClear = searchText !== ''
  const [modalText, setModalText] = useState<string | null>(null)

  const { data: leadsData, loading: leadersLoading } = useQuery(GetCommunityLeadersDocument, {
    variables: { id: communityId ?? '' },
    skip: !communityId,
  })
  const loading = loadingCommunity || leadersLoading

  const accountLeads = useMemo(() => {
    return leadsData?.community?.members?.edges
      .map(e => e?.node)
      .filter(n => !n?.user?.hidden)
      .filter(Boolean) as Membership[] | undefined
  }, [leadsData?.community?.members])

  const leadsToDisplay = useMemo(() => {
    if (accountLeads && searchText) {
      const matchedUserIds = new Set(
        Array.from(searchCache(searchText).values())
          .map(k => k.split(':'))
          .filter(e => e[0] === 'User')
          .map(m => m[1])
      )
      return accountLeads?.filter(m => matchedUserIds.has(m.userId || ''))
    }
    return sortMembers(accountLeads ?? [])
  }, [accountLeads, searchText])

  const clearSearch = () => {
    setSearchText('')
  }

  const handleSearchTextChanged = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.value !== undefined) {
        setSearchText(e.target.value)
      }
    },
    [setSearchText]
  )

  if (error) return <Alert variant="danger">{error?.message}</Alert>
  if (!leadsData && loading) return <div>Loading ...</div>

  return (
    <div className={'account-partners-container'}>
      <h3 className={'comm-title'}>Account Partners</h3>
      <ManageMembersLink />
      <div className={`table-list`} role="list">
        <div className="top-actions">
          <div className="filtering">
            <div className={`searchbar ${isCondensed ? 'condensed-searchbar' : ''} ${isMedium ? 'medium' : ''}`}>
              <img className="search-icon" src={searchIcon} alt={'Search'} />
              <Form.Control
                type="text"
                placeholder="Search Members"
                value={searchText}
                onChange={handleSearchTextChanged}
                role="searchBar"
                bsPrefix={' '}
              />
              {showSearchClear && (
                <img className={'search-clear'} src={searchClear} alt={'Clear'} title={'Clear'} onClick={clearSearch} />
              )}
            </div>
          </div>
        </div>
        {!isCondensed && (
          <div className="table-title account-partners">
            {!isNotXLarge && (
              <>
                {isVeeva ? (
                  <>
                    <span className={`semi-wide-column`}>Name & Job Title</span>
                  </>
                ) : (
                  <>
                    <span className={`semi-wide-column`}>Name & Job Title</span>
                    <span className="company-type-column">Type</span>
                    <span className="wide-column">Country</span>
                  </>
                )}
              </>
            )}
          </div>
        )}

        {leadsToDisplay && leadsToDisplay.length > 0 ? (
          <div className={'members-list'}>
            {leadsToDisplay.map(lead => (
              <CompanyMemberRow
                key={lead?.userId}
                member={lead}
                isLeadsList={true}
                isVeeva={isVeeva}
                setParentModalText={setModalText}
              />
            ))}
          </div>
        ) : (
          <>{!loading && <div>No results found</div>}</>
        )}
      </div>
      <Modal show={!!modalText} onHide={() => setModalText(null)}>
        <Modal.Header closeButton>
          <Modal.Body>
            <p>{modalText}</p>
          </Modal.Body>
        </Modal.Header>
      </Modal>
    </div>
  )
}

export default CompanyAccountPartners
